import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '@/lib/auth.service';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, Loader2, ArrowRight, ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { ROUTES } from '@/lib/constants';

export default function ForgotPassword() {
  const { t, isRTL } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const BackArrow = isRTL ? ArrowLeft : ArrowRight;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t.auth.sendResetLink;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            {sent ? (
              <MailCheck className="h-8 w-8 text-primary" />
            ) : (
              <Package className="h-8 w-8 text-primary" />
            )}
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">
              {sent ? t.auth.checkYourEmail : t.auth.forgotPasswordTitle}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {sent ? t.auth.resetEmailSent : t.auth.forgotPasswordSubtitle}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {sent ? (
            <Link
              to={ROUTES.LOGIN}
              className="flex items-center justify-center gap-2 text-sm text-primary hover:underline"
            >
              <BackArrow className="h-4 w-4" />
              {t.auth.backToLogin}
            </Link>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.email}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t.auth.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="text-end"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading || !email}>
                {loading ? (
                  <>
                    <Loader2 className="ms-2 h-4 w-4 animate-spin" />
                    {t.auth.sending}
                  </>
                ) : (
                  t.auth.sendResetLink
                )}
              </Button>

              <div className="text-center">
                <Link
                  to={ROUTES.LOGIN}
                  className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary"
                >
                  <BackArrow className="h-4 w-4" />
                  {t.auth.backToLogin}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
